import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
    useContext,
} from 'react'
import {
    Country,
    State,
    City,
} from 'country-state-city'

import ExpandableComponentContext from '../../Contexts/ExpandableComponentContext'

const countriesWithoutStates = [
    'United Kingdom',
    'Netherlands',
    'Belgium',
    'Denmark',
    'Sweden',
    'Norway',
    'Finland',
    'Ireland',
]

const countries = Country.getAllCountries()
const usCountry = countries.find(c => c.isoCode === 'US')

const DEFAULT_FIELD_CLASS = "border border-gray-300 rounded-md p-2 bg-white w-full"
const DEFAULT_READONLY_CLASS = "border border-gray-300 rounded-md p-2 bg-gray-100 w-full"

// Optional props (all default to the original behavior):
//   label             - heading text; null hides it (default "Select Location").
//   zipCodeRequired   - false makes the zip optional (default true).
//   showZipCode       - false removes the zip field entirely (value.zipCode is then '').
//   alwaysShowErrors  - show the validation error outside an ExpandableComponentContext too.
//   fieldClassName    - class for the editable selects / zip input.
//   showFieldLabels   - label each field, marked * (required) or (optional), and name the missing
//                       fields in the error message.
//   fixedCountry      - ISO code (e.g. "US"): the country is preset and shown read-only.
// Without these props the component looks and validates exactly as before (background-check forms).
// validateAndGetData() also returns `codes: { countryCode, stateCode }` next to `value`, so callers
// that need ISO codes (e.g. Stripe) get them without changing `value`'s shape for existing forms.
const LocationSelector = forwardRef(({
    data,
    readonly,
    label = "Select Location",
    zipCodeRequired = true,
    showZipCode = true,
    alwaysShowErrors = false,
    fieldClassName = DEFAULT_FIELD_CLASS,
    showFieldLabels = false,
    fixedCountry,
}, ref) => {

    const ExpandableComponentData = useContext(ExpandableComponentContext)

    const isInteracted = alwaysShowErrors || (ExpandableComponentData ? ExpandableComponentData?.isInteracted : false)

    const [states, setStates] = useState([])
    const [cities, setCities] = useState([])

    const [selectedCountry, setSelectedCountry] = useState(data?.country)
    const [selectedState, setSelectedState] = useState(data?.state)
    const [selectedCity, setSelectedCity] = useState(data?.city)
    const [zipCode, setZipCode] = useState(data?.zipCode ?? "")
    // false, or the list of missing field names ("State", "City", ...).
    const [error, setError] = useState(false)

    const isStateRequired = selectedCountry
        ? !countriesWithoutStates.includes(selectedCountry.name)
        : true

    useEffect(() => {

        let defaultCountry = usCountry

        if (fixedCountry) {
            defaultCountry = countries.find(c => c.isoCode === fixedCountry) || usCountry
        }
        else if (data?.country) {
            const preselected = countries.find(c => c.name === data?.country)
            if (preselected) {
                defaultCountry = preselected
            }
        }

        setSelectedCountry(defaultCountry)
    }, [data?.country, fixedCountry])

    useEffect(() => {
        if (!selectedCountry) {
            setStates([])
            setSelectedState(null)
            setCities([])
            setSelectedCity(null)
            return
        }

        const countryStates = State.getStatesOfCountry(selectedCountry.isoCode)
        setStates(countryStates)

        if (!isStateRequired || countryStates.length === 0) {
            setSelectedState(null)
        } else if (data?.state) {
            // Stored state may be the full name ("California") or the ISO code ("CA"), and can
            // differ in casing, so match on both rather than only an exact name — otherwise the
            // dropdown stays empty and (since cities derive from the state) the city blanks too.
            const target = String(data.state).trim().toLowerCase()
            const matchedState = countryStates.find(
                s => s.name.toLowerCase() === target || s.isoCode.toLowerCase() === target,
            )
            setSelectedState(matchedState || null)
        } else {
            setSelectedState(null)
        }

        setCities([])
        setSelectedCity(null)
    }, [selectedCountry, data?.state, isStateRequired])

    useEffect(() => {
        if (!selectedCountry) {
            setCities([])
            setSelectedCity(null)
            return
        }

        let cityList = []

        if (!isStateRequired) {
            cityList = City.getCitiesOfCountry(selectedCountry.isoCode)
        } else if (selectedState) {
            cityList = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode)
        }

        setCities(cityList)

        if (data?.city) {
            const target = String(data.city).trim().toLowerCase()
            const matchedCity = cityList.find(c => c.name.toLowerCase() === target)
            setSelectedCity(matchedCity || null)
        } else {
            setSelectedCity(null)
        }
    }, [selectedState, selectedCountry, data?.city, isStateRequired])

    useEffect(() => {
        setZipCode(data?.zipCode || '')
    }, [data?.zipCode])

    // Which fields must be filled for the current selection.
    const stateRequired = isStateRequired
    const cityRequired = cities.length > 0 || (stateRequired && !selectedState)

    // With showFieldLabels each field gets a wrapper + label; without it the markup is unchanged.
    const wrapField = (text, required, field) => showFieldLabels
        ? <div>{fieldLabel(text, required)}{field}</div>
        : field

    const fieldLabel = (text, required) => showFieldLabels && (
        <label className="block text-xs font-medium text-gray-600 mb-1">
            {text}
            {required
                ? <span className="text-red-500"> *</span>
                : <span className="text-gray-400 font-normal"> (optional)</span>}
        </label>
    )

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            const finalZip = zipCode.trim()

            const missing = []
            if (!selectedCountry) missing.push('Country')
            if (stateRequired && !selectedState) missing.push('State')
            if (cityRequired && !selectedCity) missing.push('City')
            if (showZipCode && zipCodeRequired && finalZip === '') missing.push('Zip Code')

            const isValid = missing.length === 0

            if (isValid) {
                setError(false)
            }
            else {
                if (isInteracted) {
                    setError(missing)
                }
            }

            return {
                isValid: !!isValid,
                value: {
                    country: selectedCountry?.name,
                    state: selectedState?.name || '',
                    city: selectedCity?.name,
                    zipCode: showZipCode ? finalZip : ''
                },
                codes: {
                    countryCode: selectedCountry?.isoCode || '',
                    stateCode: selectedState?.isoCode || '',
                },
            }
        }
    }))

    return (
        <div>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label}
                </label>
            )}

            <div className="grid grid-cols-2 gap-4">
                {wrapField('Country', true, (
                    readonly || fixedCountry ? (
                        <input
                            type="text"
                            readOnly
                            className={DEFAULT_READONLY_CLASS}
                            value={selectedCountry?.name || ''}
                        />
                    ) : (
                        <select
                            className={fieldClassName}
                            value={selectedCountry?.isoCode || ''}
                            onChange={e => {
                                const selected = countries.find(c => c.isoCode === e.target.value)
                                setSelectedCountry(selected)
                                setSelectedState(null)
                                setSelectedCity(null)
                                setCities([])
                                setZipCode('')
                                setError(false)
                            }}
                        >
                            <option value="">Select Country</option>
                            {countries.map(country => (
                                <option key={country.isoCode} value={country.isoCode}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                    )
                ))}

                {wrapField('State', stateRequired, (
                    readonly ? (
                        <input
                            type="text"
                            readOnly
                            className={DEFAULT_READONLY_CLASS}
                            value={selectedState?.name || ''}
                        />
                    ) : (
                        <select
                            className={fieldClassName}
                            value={selectedState?.isoCode || ''}
                            onChange={e => {
                                const selected = states.find(s => s.isoCode === e.target.value)
                                setSelectedState(selected)
                                setSelectedCity(null)
                                setCities([])
                                setZipCode('')
                                setError(false)
                            }}
                            disabled={!isStateRequired || states.length === 0}
                        >
                            <option value="">Select State</option>
                            {states.map(state => (
                                <option key={state.isoCode} value={state.isoCode}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                    )
                ))}

                {wrapField('City', cityRequired, (
                    readonly ? (
                        <input
                            type="text"
                            readOnly
                            className={DEFAULT_READONLY_CLASS}
                            value={selectedCity?.name || ''}
                        />
                    ) : (
                        <select
                            className={fieldClassName}
                            value={selectedCity?.name || ''}
                            onChange={e => {
                                const selected = cities.find(c => c.name === e.target.value)
                                setSelectedCity(selected)
                                setZipCode('')
                                setError(false)
                            }}
                            disabled={cities.length === 0}
                        >
                            <option value="">Select City</option>
                            {cities.map(city => (
                                <option key={city.name} value={city.name}>
                                    {city.name}
                                </option>
                            ))}
                        </select>
                    )
                ))}

                {showZipCode && (
                    wrapField('Zip Code', zipCodeRequired, (
                        readonly ? (
                            <input
                                type="text"
                                readOnly
                                className={DEFAULT_READONLY_CLASS}
                                value={zipCode}
                            />
                        ) : (
                            <input
                                type="text"
                                className={fieldClassName}
                                placeholder={zipCodeRequired ? 'Enter Zip Code, fill "00000" if NA' : 'Zip Code (optional)'}
                                value={zipCode}
                                onChange={e => {
                                    setZipCode(e.target.value)
                                    setError(false)
                                }}
                            />
                        )
                    ))
                )}
            </div>

            {error && (
                <p className="text-sm text-red-600 mt-2">
                    {showFieldLabels && Array.isArray(error) && error.length
                        ? `Please fill in: ${error.join(', ')}`
                        : 'Fill location fields correctly'}
                </p>
            )}
        </div>
    )
})

export default LocationSelector
